import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/constants";
import { toast } from "sonner";
import { ShieldCheck, Lock, CheckCircle2, Undo2, PackageCheck } from "lucide-react";

const loadCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export function EscrowPanel({ campaign, isOwner, isSelected, onChange }) {
  const [busy, setBusy] = useState(false);
  const st = campaign.escrow_status;
  const money = formatMoney(campaign.escrow_amount, campaign.escrow_currency);

  const call = async (path, msg) => {
    setBusy(true);
    try { await api.post(path); toast.success(msg); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  const fund = async () => {
    setBusy(true);
    try {
      if (!(await loadCheckout())) throw new Error("Could not load Razorpay checkout.");
      const { data } = await api.post(`/escrow/${campaign.campaign_id}/order`);
      const rz = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency,
        name: "InfluencerCollab", description: `Escrow · ${data.description}`,
        prefill: { name: data.name, email: data.email }, theme: { color: "#FF2E93" },
        handler: async (res) => {
          try { await api.post("/escrow/verify", res); toast.success("Payment received — funds are held in escrow."); onChange(); }
          catch (e) { toast.error(e?.response?.data?.detail || "Payment verification failed."); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rz.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed."));
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start payment.");
      setBusy(false);
    }
  };

  if (!isOwner && !isSelected) return null;
  return (
    <div className="mt-4 rounded-xl border border-border bg-background p-4 text-sm space-y-3" data-testid="escrow-panel">
      <p className="font-semibold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-neon-pink" /> Escrow</p>
      {(!st || st === "pending") && (
        isOwner ? (
          <>
            <p className="text-muted-foreground">Pay the agreed amount into escrow. It's released to the creator only when you approve the work.</p>
            <Button onClick={fund} disabled={busy} data-testid="fund-escrow-btn" className="w-full rounded-full bg-neon-pink text-white hover:bg-neon-pink/90 font-bold"><Lock className="h-4 w-4 mr-1" /> {busy ? "Opening…" : "Fund escrow"}</Button>
          </>
        ) : <p className="text-muted-foreground" data-testid="escrow-status-text">Waiting for the promoter to fund escrow.</p>
      )}
      {st === "funded" && (
        <>
          <p data-testid="escrow-status-text"><span className="font-semibold">{money}</span> held in escrow{campaign.work_delivered ? " · work delivered" : ""}</p>
          {isSelected && !campaign.work_delivered && <Button onClick={() => call(`/escrow/${campaign.campaign_id}/deliver`, "Marked as delivered.")} disabled={busy} data-testid="mark-delivered-btn" className="w-full rounded-full"><PackageCheck className="h-4 w-4 mr-1" /> Mark work delivered</Button>}
          {isOwner && (
            <div className="flex flex-col gap-2">
              <Button onClick={() => call(`/escrow/${campaign.campaign_id}/release`, "Payment released to the creator.")} disabled={busy} data-testid="release-escrow-btn" className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-semibold"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve & release</Button>
              <Button variant="outline" onClick={() => window.confirm("Refund the full escrow amount and close this campaign?") && call(`/escrow/${campaign.campaign_id}/refund`, "Refund initiated.")} disabled={busy} data-testid="refund-escrow-btn" className="rounded-full"><Undo2 className="h-4 w-4 mr-1" /> Request refund</Button>
            </div>
          )}
        </>
      )}
      {st === "released" && <p className="text-neon-lime font-semibold" data-testid="escrow-status-text">{money} released · campaign completed</p>}
      {st === "refunded" && <p className="text-muted-foreground" data-testid="escrow-status-text">{money} refunded to the promoter</p>}
    </div>
  );
}
